import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { getTasks, createTask, updateTask, updateTaskStatus, deleteTask, getOrders, getTaskComments, createTaskComment, updateTaskComment, deleteTaskComment, getAssignableUsers, getCurrentUser } from '@/lib/api';
import { Plus, Search, CheckSquare, Trash2, Edit2, ExternalLink, ArrowUp, ArrowDown, X, ChevronsUpDown, MessageCircle, Send, Smile, MoreVertical, AtSign, Maximize2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatDateWord } from '@/lib/dateFormat';
import DateField from '@/components/common/DateField';

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-600/80 text-white border-slate-600' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-600/80 text-white border-blue-600' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-600/80 text-white border-emerald-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-900/80 text-white border-red-900' },
];

const priorityOptions = [
  { value: 'low', label: 'Low', color: 'bg-blue-600/80 text-white border-blue-600' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-600/80 text-white border-amber-600' },
  { value: 'high', label: 'High', color: 'bg-orange-700/80 text-white border-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-900/80 text-white border-red-900' },
];

const storeOptions = [
  { value: 'general', label: 'General', color: 'bg-slate-600/80 text-white border-slate-600' },
  { value: 'store_perth', label: 'Perth', color: 'bg-purple-600/80 text-white border-purple-600' },
  { value: 'store_singapore', label: 'Singapore', color: 'bg-teal-600/80 text-white border-teal-600' },
];

const entityTypeOptions = [
  { value: 'general', label: 'General' },
  { value: 'order', label: 'Order' },
  { value: 'customer', label: 'Customer' },
  { value: 'garment', label: 'Garment' },
];

const getStatusConfig = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
const getPriorityConfig = (priority) => priorityOptions.find(p => p.value === priority) || priorityOptions[1];
const getStoreConfig = (store) => {
  if (!store || store === 'general') return storeOptions[0];
  const storeOption = storeOptions.find(s => s.value === store);
  return storeOption || storeOptions[0];
};

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userStores, setUserStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to active (hide completed)
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [descriptionModalTask, setDescriptionModalTask] = useState(null);
  
  // Comment states
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskComments, setTaskComments] = useState({});
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  
  // Sorting state - load from localStorage or use defaults
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('tasksSortConfig');
    return saved ? JSON.parse(saved) : { column: 'due_date', direction: 'asc', secondarySort: 'priority' };
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: [],  // Changed to array for multiple users
    due_date: '',
    priority: 'medium',
    associated_entity_type: '',  // Empty by default - user must select
    associated_entity_id: '',
    store: null,  // null by default, must be selected
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchTasks();
    fetchOrders();
    fetchUsers();
  }, [statusFilter, storeFilter]);

  // SSE Real-time updates
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.warn('⚠️ No auth token found, skipping SSE connection');
      return;
    }
    
    // Create EventSource for SSE
    const sseUrl = `${backendUrl}/api/tasks/stream?token=${token}`;
    const eventSource = new EventSource(sseUrl);
    
    eventSource.addEventListener('connected', (event) => {
      console.log('✅ Connected to tasks stream');
    });
    
    eventSource.addEventListener('task_update', (event) => {
      try {
        const payload = JSON.parse(event.data);
        const { updates } = payload;
        
        console.log(`📡 Received ${updates.length} task update(s) from SSE`);
        
        // Apply updates to local state
        setTasks(prevTasks => {
          const updatedTasks = [...prevTasks];
          
          updates.forEach(update => {
            const { type, data } = update;
            const taskIndex = updatedTasks.findIndex(t => t.task_id === data.task_id);
            
            if (taskIndex !== -1) {
              // Update existing task
              if (type === 'task_updated') {
                updatedTasks[taskIndex] = {
                  ...updatedTasks[taskIndex],
                  ...data.changes
                };
                console.log(`✅ Updated task ${data.task_id}`);
              } else if (type === 'task_status_updated') {
                updatedTasks[taskIndex] = {
                  ...updatedTasks[taskIndex],
                  status: data.status
                };
                console.log(`✅ Updated task status ${data.task_id} to ${data.status}`);
              }
            }
          });
          
          return updatedTasks;
        });
      } catch (error) {
        console.error('❌ Error processing task update:', error);
      }
    });
    
    eventSource.onerror = (error) => {
      console.error('❌ SSE connection error:', error);
    };
    
    // Cleanup on unmount
    return () => {
      eventSource.close();
      console.log('🔌 Disconnected from tasks stream');
    };
  }, []); // Empty deps - only connect once

  const fetchCurrentUser = async () => {
    try {
      const response = await getCurrentUser();
      setCurrentUser(response.data);
      setUserStores(response.data.stores || []);
    } catch (error) {
      console.error('Failed to fetch current user');
    }
  };

  // Compute available store options based on user access
  const availableStoreOptions = useMemo(() => {
    if (!currentUser) return storeOptions;
    
    // Owner and admin see all stores
    if (currentUser.role === 'owner' || currentUser.role === 'admin') {
      return storeOptions;
    }
    
    // Other users only see their assigned stores + general
    const assignedStores = currentUser.assigned_stores || [];
    
    // Always include "General"
    const filtered = [storeOptions[0]]; // General option
    
    // Add user's assigned stores
    assignedStores.forEach(storeId => {
      const storeOption = storeOptions.find(s => s.value === storeId);
      if (storeOption) {
        filtered.push(storeOption);
      }
    });
    
    return filtered;
  }, [currentUser]);

  const fetchTasks = async () => {
    try {
      // Pass store filter to backend
      const backendStatusFilter = (statusFilter === 'all' || statusFilter === 'active') ? null : statusFilter;
      const backendStoreFilter = storeFilter === 'all' ? null : storeFilter;
      const response = await getTasks(backendStatusFilter, backendStoreFilter);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await getOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getAssignableUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Related To field
    if (!formData.associated_entity_type) {
      toast.error('Please select a "Related To" type');
      return;
    }
    
    // Validate order selection if type is order
    if (formData.associated_entity_type === 'order' && !formData.associated_entity_id) {
      toast.error('Please select an order');
      return;
    }
    
    // Validate store selection (mandatory for non-order and non-general types)
    // General entity type can have null store (it will be set to general/null)
    if (formData.store === null && formData.associated_entity_type !== 'order' && formData.associated_entity_type !== 'general') {
      toast.error('Please select a Store');
      return;
    }
    
    try {
      // Send assigned_to as array directly to backend
      const taskData = {
        ...formData,
        assigned_to: formData.assigned_to || [],  // Send as array
        store: formData.store === 'general' ? null : formData.store,
      };
      
      if (editingTask) {
        await updateTask(editingTask.task_id, taskData);
        toast.success('Task updated successfully');
      } else {
        await createTask(taskData);
        toast.success('Task created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    
    // Convert comma-separated string to array for editing
    let assignedArray = [];
    if (task.assigned_to) {
      if (Array.isArray(task.assigned_to)) {
        assignedArray = task.assigned_to;
      } else if (typeof task.assigned_to === 'string') {
        assignedArray = task.assigned_to.split(',').map(u => u.trim()).filter(Boolean);
      }
    }
    
    setFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: assignedArray,
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority || 'medium',
      associated_entity_type: task.associated_entity_type || '',
      associated_entity_id: task.associated_entity_id || '',
      store: task.store || null,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteTask(taskId);
      toast.success('Task deleted successfully');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(t => 
          t.task_id === taskId ? { ...t, status: newStatus } : t
        )
      );
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (taskId, newPriority) => {
    try {
      // Get the task
      const task = tasks.find(t => t.task_id === taskId);
      if (!task) return;

      // Update the task with new priority
      const taskData = {
        title: task.title,
        description: task.description,
        assigned_to: task.assigned_to,
        due_date: task.due_date,
        priority: newPriority,
        associated_entity_type: task.associated_entity_type,
        associated_entity_id: task.associated_entity_id,
      };
      
      await updateTask(taskId, taskData);
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(t => 
          t.task_id === taskId ? { ...t, priority: newPriority } : t
        )
      );
      toast.success('Priority updated');
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleAssignedToChange = async (taskId, newAssignedTo) => {
    try {
      // Get the task
      const task = tasks.find(t => t.task_id === taskId);
      if (!task) return;

      // Send assigned_to as array directly
      const assignedToArray = Array.isArray(newAssignedTo) 
        ? newAssignedTo 
        : [newAssignedTo];

      // Update the task with new assigned users
      const taskData = {
        title: task.title,
        description: task.description,
        assigned_to: assignedToArray,  // Send as array
        due_date: task.due_date,
        priority: task.priority,
        associated_entity_type: task.associated_entity_type,
        associated_entity_id: task.associated_entity_id,
      };
      
      await updateTask(taskId, taskData);
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(t => 
          t.task_id === taskId ? { ...t, assigned_to: assignedToArray } : t
        )
      );
      toast.success('Assigned users updated');
    } catch (error) {
      toast.error('Failed to update assigned users');
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      assigned_to: [],  // Array for multiple users
      due_date: '',
      priority: 'medium',
      associated_entity_type: '',  // Empty - user must select
      associated_entity_id: '',
      store: null,  // null - user must select
    });
  };

  // Get all users for assignment and mentions
  const allUsers = users.map(u => u.name || u.email);

  // Get unique assigned users for filter - handle comma-separated strings
  const assignedUsers = [...new Set(
    tasks.flatMap(t => {
      if (!t.assigned_to) return [];
      if (Array.isArray(t.assigned_to)) return t.assigned_to;
      return t.assigned_to.split(',').map(u => u.trim());
    })
  )].filter(Boolean);

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tasksSortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        // Toggle direction if same column
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        // New column, default to asc
        return { column, direction: 'asc', secondarySort: column === 'due_date' ? 'priority' : null };
      }
    });
  };

  // Clear all sorting
  const clearSort = () => {
    setSortConfig({ column: 'due_date', direction: 'asc', secondarySort: 'priority' });
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    // Status filter
    if (statusFilter === 'active') {
      if (task.status === 'completed' || task.status === 'cancelled') return false;
    } else if (statusFilter !== 'all' && task.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const assignedNames = Array.isArray(task.assigned_to) 
        ? task.assigned_to.join(' ') 
        : (task.assigned_to || '');
      const searchable = `${task.title} ${task.description || ''} ${assignedNames}`.toLowerCase();
      if (!searchable.includes(search)) return false;
    }
    // Priority filter
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
    // Assigned filter - handle both array and string formats
    if (assignedFilter !== 'all') {
      const taskUsers = task.assigned_to 
        ? (Array.isArray(task.assigned_to) 
            ? task.assigned_to 
            : (typeof task.assigned_to === 'string' 
                ? task.assigned_to.split(',').map(u => u.trim()) 
                : [task.assigned_to]))
        : [];
      if (!taskUsers.includes(assignedFilter)) return false;
    }
    return true;
  });

  // Sort tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const column = sortConfig.column;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    let comparison = 0;
    
    if (column === 'due_date') {
      const dateA = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
      const dateB = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
      comparison = dateA - dateB;
    } else if (column === 'priority') {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      comparison = (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
    } else if (column === 'status') {
      comparison = (a.status || '').localeCompare(b.status || '');
    } else if (column === 'title') {
      comparison = (a.title || '').localeCompare(b.title || '');
    } else if (column === 'assigned_to') {
      // Handle array sorting - join arrays for comparison
      const assignedA = Array.isArray(a.assigned_to) 
        ? a.assigned_to.join(', ') 
        : (a.assigned_to || '');
      const assignedB = Array.isArray(b.assigned_to) 
        ? b.assigned_to.join(', ') 
        : (b.assigned_to || '');
      comparison = assignedA.localeCompare(assignedB);
    } else if (column === 'created_by') {
      comparison = (a.created_by || '').localeCompare(b.created_by || '');
    }
    
    if (comparison !== 0) return comparison * direction;
    
    // Secondary sort by priority if primary was due_date
    if (sortConfig.secondarySort === 'priority') {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
    }
    
    return 0;
  });

  // Get display name for related entity
  const getRelatedEntityDisplay = (task) => {
    const entityType = task.associated_entity_type?.toLowerCase();
    
    if (!entityType || entityType === 'general') {
      return { type: 'general', label: 'General', link: null };
    }
    if (entityType === 'order' && task.associated_entity_id) {
      // Use associated_entity_label from backend (order_number)
      const orderNumber = task.associated_entity_label || 'Order';
      const order = orders.find(o => o.order_id === task.associated_entity_id);
      const customerName = task.associated_order_customer_name || order?.customer_name;
      const customerNo = task.associated_order_customer_no || order?.customer_no;
      return {
        type: 'order',
        orderNumber: orderNumber,
        customerName,
        customerNo,
        link: `/orders/${task.associated_entity_id}/edit`,
      };
    }
    if (entityType === 'customer') {
      return { type: 'customer', label: 'Customer', link: null };
    }
    if (entityType === 'garment') {
      const garmentNo = task.associated_entity_label || 'Garment';
      return { type: 'garment', label: garmentNo, link: null };
    }
    if (entityType === 'fabric_order') {
      const fabricId = task.associated_entity_label || 'Fabric Order';
      return { type: 'fabric_order', label: fabricId, link: null };
    }
    return { type: 'other', label: task.associated_entity_type, link: null };
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get colorful avatar background based on name
  const getAvatarColor = (name) => {
    if (!name) return 'bg-slate-500';
    
    const colors = [
      'bg-blue-600',
      'bg-purple-600',
      'bg-pink-600',
      'bg-green-600',
      'bg-yellow-600',
      'bg-red-600',
      'bg-indigo-600',
      'bg-teal-600',
      'bg-orange-600',
      'bg-cyan-600',
    ];
    
    // Simple hash function to consistently assign colors based on name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Fetch comments for a task
  const fetchComments = async (taskId) => {
    setLoadingComments(true);
    try {
      const response = await getTaskComments(taskId);
      setTaskComments(prev => ({
        ...prev,
        [taskId]: response.data
      }));
    } catch (error) {
      toast.error('Failed to fetch comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // Toggle task expansion
  const handleToggleExpand = async (taskId) => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
      setCommentText('');
      setShowEmojiPicker(false);
      setShowMentions(false);
    } else {
      setExpandedTaskId(taskId);
      if (!taskComments[taskId]) {
        await fetchComments(taskId);
      }
    }
  };

  // Handle comment text change with @mention detection
  const handleCommentTextChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    
    setCommentText(value);
    setCursorPosition(position);
    
    // Detect @ mention
    const textBeforeCursor = value.substring(0, position);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1 && (atIndex === 0 || /\s/.test(textBeforeCursor[atIndex - 1]))) {
      const searchText = textBeforeCursor.substring(atIndex + 1);
      if (!searchText.includes(' ')) {
        setMentionSearch(searchText);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  // Insert mention
  const insertMention = (userName) => {
    const textBeforeCursor = commentText.substring(0, cursorPosition);
    const textAfterCursor = commentText.substring(cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    const newText = 
      textBeforeCursor.substring(0, atIndex) + 
      `@${userName} ` + 
      textAfterCursor;
    
    setCommentText(newText);
    setShowMentions(false);
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Filter users for mention autocomplete
  const filteredMentionUsers = allUsers.filter(user => 
    user.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  // Add comment
  const handleAddComment = async (taskId) => {
    if (!commentText.trim()) return;
    
    try {
      const response = await createTaskComment(taskId, { comment_text: commentText });
      setTaskComments(prev => ({
        ...prev,
        [taskId]: [...(prev[taskId] || []), response.data]
      }));
      // Update task comment_count in local state
      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.task_id === taskId ? { ...t, comment_count: (t.comment_count || 0) + 1 } : t
        )
      );
      setCommentText('');
      setShowEmojiPicker(false);
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Start editing comment
  const handleStartEditComment = (comment) => {
    setEditingComment(comment);
    setEditCommentText(comment.comment_text);
  };

  // Save edited comment
  const handleSaveEditComment = async (taskId, commentId) => {
    if (!editCommentText.trim()) return;
    
    try {
      const response = await updateTaskComment(taskId, commentId, { comment_text: editCommentText });
      setTaskComments(prev => ({
        ...prev,
        [taskId]: prev[taskId].map(c => c.comment_id === commentId ? response.data : c)
      }));
      setEditingComment(null);
      setEditCommentText('');
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  // Delete comment
  const handleDeleteComment = async (taskId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await deleteTaskComment(taskId, commentId);
      setTaskComments(prev => ({
        ...prev,
        [taskId]: prev[taskId].filter(c => c.comment_id !== commentId)
      }));
      // Update task comment_count in local state
      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.task_id === taskId ? { ...t, comment_count: Math.max(0, (t.comment_count || 1) - 1) } : t
        )
      );
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    setCommentText(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Get current user email for permission checks
  const getCurrentUserEmail = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.email;
    } catch {
      return null;
    }
  };

  // Get comment count for a task
  const getCommentCount = (task) => {
    // Use fetched comments count if available, otherwise use count from API
    if (taskComments[task.task_id]) {
      return taskComments[task.task_id].length;
    }
    return task.comment_count || 0;
  };

  const canDeleteTask = (task) => {
    if (!currentUser) return false;
    const role = (currentUser.role || '').toLowerCase();
    if (role === 'owner' || role === 'admin') return true;

    const createdBy = (task.created_by || '').toLowerCase();
    const userName = (currentUser.name || '').toLowerCase();
    const userEmail = (currentUser.email || '').toLowerCase();
    return !!createdBy && (createdBy === userName || createdBy === userEmail);
  };

  const formatDueDate = (dueDateValue) => {
    return formatDateWord(dueDateValue);
  };

  const isDescriptionLong = (description) => (description || '').trim().length > 120;

  const toggleDescription = (taskId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  return (
    <div className="space-y-6 p-3" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Tasks
          </h1>
          <p className="text-muted-foreground text-sm">Manage your team's tasks and priorities.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-blue-600 text-white hover:bg-blue-700"
              data-testid="add-task-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {editingTask ? 'Edit Task' : 'Create Task'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title *</Label>
                <Input
                  data-testid="task-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Task title"
                  className="h-9 bg-background border-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea
                  data-testid="task-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Task description..."
                  className="min-h-[80px] bg-background border-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Assigned To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full h-11 justify-between font-normal"
                      >
                        {formData.assigned_to.length > 0 ? (
                          <span className="truncate">
                            {formData.assigned_to.length} user(s) selected
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Select users...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search users..." />
                        <CommandEmpty>No user found.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {allUsers.map((user) => (
                            <CommandItem
                              key={user}
                              onSelect={() => {
                                const newAssigned = formData.assigned_to.includes(user)
                                  ? formData.assigned_to.filter(u => u !== user)
                                  : [...formData.assigned_to, user];
                                setFormData({ ...formData, assigned_to: newAssigned });
                              }}
                            >
                              <Checkbox
                                checked={formData.assigned_to.includes(user)}
                                className="mr-2"
                              />
                              {user}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {formData.assigned_to.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.assigned_to.map(user => (
                        <Badge key={user} variant="secondary" className="text-xs">
                          {user}
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, assigned_to: formData.assigned_to.filter(u => u !== user) })}
                            className="ml-1 hover:text-red-600"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Due Date</Label>
                  <DateField
                    testId="task-due-date-input"
                    value={formData.due_date}
                    onChange={(next) => setFormData({ ...formData, due_date: next })}
                    className="h-11 w-full"
                    allowClear={false}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="task-priority-select" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Related To *</Label>
                  <Select
                    value={formData.associated_entity_type}
                    onValueChange={(value) => setFormData({ ...formData, associated_entity_type: value, associated_entity_id: '' })}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {entityTypeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formData.associated_entity_type === 'order' && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Select Order *</Label>
                  <Select
                    value={formData.associated_entity_id}
                    onValueChange={(value) => setFormData({ ...formData, associated_entity_id: value })}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select an order..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {orders.map(order => (
                        <SelectItem key={order.order_id} value={order.order_id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{order.order_number}</span>
                            <span className="text-xs text-muted-foreground">
                              {order.customer_name} • {order.status}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Store will be auto-populated from the selected order
                  </p>
                </div>
              )}
              {formData.associated_entity_type && formData.associated_entity_type !== 'order' && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Store *</Label>
                  <Select
                    value={formData.store === null ? 'general' : formData.store}
                    onValueChange={(value) => setFormData({ ...formData, store: value === 'general' ? null : value })}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select store..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStoreOptions.map(store => (
                        <SelectItem key={store.value} value={store.value}>{store.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select "General" for private tasks visible only to you and assigned users
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-task-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-border bg-card shadow-sm rounded-xl">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="task-search-input"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            <div className="flex gap-3 flex-wrap items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="task-status-filter" className="w-[140px] h-10">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px] h-10">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {priorityOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                <SelectTrigger className="w-[140px] h-10">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {assignedUsers.map(user => (
                    <SelectItem key={user} value={user}>{user}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="w-[140px] h-10">
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {availableStoreOptions.map(store => (
                    <SelectItem key={store.value} value={store.value}>{store.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(sortConfig.column !== 'due_date' || sortConfig.direction !== 'asc' || !sortConfig.secondarySort) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSort}
                  className="h-10"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear Sort
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card shadow-sm rounded-xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : sortedTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckSquare className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No tasks found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'Try a different search term' : 'Create your first task to get started'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                  {/* Title - Sortable */}
                  <TableHead 
                    className="text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {sortConfig.column === 'title' && (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  {/* Related To */}
                  <TableHead className="text-xs font-semibold uppercase tracking-wider">Related To</TableHead>
                  {/* Due Date - Sortable */}
                  <TableHead 
                    className="text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('due_date')}
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      {sortConfig.column === 'due_date' && (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  {/* Assigned To - Sortable */}
                  <TableHead 
                    className="text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('assigned_to')}
                  >
                    <div className="flex items-center gap-1">
                      Assigned To
                      {sortConfig.column === 'assigned_to' && (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  {/* Priority - Sortable */}
                  <TableHead 
                    className="text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center gap-1">
                      Priority
                      {sortConfig.column === 'priority' && (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  {/* Created By - Sortable */}
                  <TableHead 
                    className="text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('created_by')}
                  >
                    <div className="flex items-center gap-1">
                      Created By
                      {sortConfig.column === 'created_by' && (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  {/* Store */}
                  <TableHead className="text-xs font-semibold uppercase tracking-wider">Store</TableHead>
                  {/* Status */}
                  <TableHead className="text-xs font-semibold uppercase tracking-wider">Status</TableHead>
                  {/* Actions */}
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTasks.map((task) => {
                  const statusConfig = getStatusConfig(task.status);
                  const priorityConfig = getPriorityConfig(task.priority);
                  const relatedEntity = getRelatedEntityDisplay(task);
                  const isExpanded = expandedTaskId === task.task_id;
                  const commentCount = getCommentCount(task);

                  return (
                    <>
                      <TableRow key={task.task_id} className="border-slate-200 dark:border-white/5">
                        {/* Title */}
                        <TableCell>
                          <div className="space-y-0.5">
                            <p className="font-medium">{task.title}</p>
                            {task.description && (
                              <div className="space-y-1 max-w-[340px]">
                                <p
                                  className={`text-sm text-muted-foreground whitespace-pre-wrap break-words ${expandedDescriptions[task.task_id] ? '' : 'max-h-10 overflow-hidden'}`}
                                  data-testid={`task-description-${task.task_id}`}
                                >
                                  {task.description}
                                </p>
                                {isDescriptionLong(task.description) && (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="link"
                                      className="h-auto p-0 text-xs"
                                      onClick={() => toggleDescription(task.task_id)}
                                      data-testid={`task-description-toggle-${task.task_id}`}
                                    >
                                      {expandedDescriptions[task.task_id] ? 'Show less' : 'Show more'}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => setDescriptionModalTask(task)}
                                      data-testid={`task-description-modal-open-${task.task_id}`}
                                    >
                                      <Maximize2 className="w-3 h-3 mr-1" />
                                      Full view
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>

                        {/* Related To */}
                        <TableCell>
                          {relatedEntity.type === 'order' && relatedEntity.link ? (
                            <Link 
                              to={relatedEntity.link}
                              className="text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-sm">{relatedEntity.orderNumber}</span>
                                  <ExternalLink className="w-3 h-3" />
                                </div>
                                {(relatedEntity.customerName || relatedEntity.customerNo) && (
                                  <p className="text-xs text-muted-foreground" data-testid={`task-related-order-customer-${task.task_id}`}>
                                    {relatedEntity.customerName || 'Customer'}
                                    {relatedEntity.customerNo ? ` (${relatedEntity.customerNo})` : ''}
                                  </p>
                                )}
                              </div>
                            </Link>
                          ) : relatedEntity.type === 'general' ? (
                            <span className="text-muted-foreground text-sm">{relatedEntity.label}</span>
                          ) : (
                            <span className="text-sm">{relatedEntity.label}</span>
                          )}
                        </TableCell>

                        {/* Due Date */}
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDueDate(task.due_date)}
                        </TableCell>

                        {/* Assigned To - Inline Editable */}
                        <TableCell>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button 
                                variant="ghost" 
                                className="h-auto p-1 hover:bg-muted"
                              >
                                {task.assigned_to ? (
                                  <div className="flex items-center gap-1">
                                    {(() => {
                                      const users = typeof task.assigned_to === 'string' 
                                        ? task.assigned_to.split(',').map(u => u.trim()).filter(Boolean)
                                        : (Array.isArray(task.assigned_to) ? task.assigned_to : [task.assigned_to]);
                                      
                                      if (users.length === 0) return <span className="text-muted-foreground">—</span>;
                                      
                                      return (
                                        <>
                                          {users.slice(0, 2).map((user, idx) => (
                                            <Avatar key={idx} className="h-8 w-8">
                                              <AvatarFallback className={`${getAvatarColor(user)} text-white text-xs font-medium`}>
                                                {getInitials(user)}
                                              </AvatarFallback>
                                            </Avatar>
                                          ))}
                                          {users.length > 2 && (
                                            <span className="text-xs text-muted-foreground ml-1">
                                              +{users.length - 2}
                                            </span>
                                          )}
                                          {users.length === 1 && (
                                            <span className="text-sm ml-1">{users[0]}</span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[280px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search users..." />
                                <CommandEmpty>No user found.</CommandEmpty>
                                <CommandList>
                                  <CommandGroup className="max-h-64 overflow-auto">
                                    {allUsers.map((user) => {
                                      const currentUsers = task.assigned_to 
                                        ? (typeof task.assigned_to === 'string' 
                                            ? task.assigned_to.split(',').map(u => u.trim()).filter(Boolean)
                                            : (Array.isArray(task.assigned_to) ? task.assigned_to : [task.assigned_to]))
                                        : [];
                                      
                                      const isSelected = currentUsers.includes(user);
                                      
                                      return (
                                        <CommandItem
                                          key={user}
                                          onSelect={() => {
                                            const newUsers = isSelected
                                              ? currentUsers.filter(u => u !== user)
                                              : [...currentUsers, user];
                                            handleAssignedToChange(task.task_id, newUsers);
                                          }}
                                        >
                                          <Checkbox
                                            checked={isSelected}
                                            className="mr-2"
                                          />
                                          <Avatar className="h-6 w-6 mr-2">
                                            <AvatarFallback className={`${getAvatarColor(user)} text-white text-[10px] font-medium`}>
                                              {getInitials(user)}
                                            </AvatarFallback>
                                          </Avatar>
                                          {user}
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </TableCell>

                        {/* Priority - Inline Editable */}
                        <TableCell>
                          <Select
                            value={task.priority}
                            onValueChange={(value) => handlePriorityChange(task.task_id, value)}
                          >
                            <SelectTrigger className="h-7 w-[100px] border-0 hover:bg-muted">
                              <Badge variant="outline" className={`${priorityConfig.color} capitalize text-xs`}>
                                {priorityConfig.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {priorityOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        {/* Created By */}
                        <TableCell>
                          <span className="text-sm">{task.created_by || '—'}</span>
                        </TableCell>

                        {/* Store */}
                        <TableCell>
                          {(() => {
                            const storeConfig = getStoreConfig(task.store);
                            return (
                              <Badge variant="outline" className={`${storeConfig.color} capitalize text-xs`}>
                                {storeConfig.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>

                        {/* Status - Inline Editable */}
                        <TableCell>
                          <Select
                            value={task.status}
                            onValueChange={(value) => handleStatusChange(task.task_id, value)}
                          >
                            <SelectTrigger className="h-7 w-[120px] border-0 hover:bg-muted">
                              <Badge variant="outline" className={`${statusConfig.color} capitalize text-xs`}>
                                {statusConfig.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`h-8 w-8 ${isExpanded ? 'text-blue-600 bg-blue-50' : 'text-slate-600'} hover:text-blue-700 hover:bg-blue-50 relative`}
                              onClick={() => handleToggleExpand(task.task_id)}
                              title="Comments"
                            >
                              <MessageCircle className="w-4 h-4" />
                              {commentCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                  {commentCount}
                                </span>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleEdit(task)}
                              title="Edit task"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(task.task_id)}
                              disabled={!canDeleteTask(task)}
                              title="Delete task"
                              data-testid={`delete-task-${task.task_id}-button`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Comment Section */}
                      {isExpanded && (
                        <TableRow key={`${task.task_id}-comments`} className="bg-[#f7fafc]">
                          <TableCell colSpan={9} className="p-0">
                            <div className="p-6 space-y-4 border-l-4 border-blue-500 bg-[#f7fafc]">
                              {/* Comments List */}
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {loadingComments ? (
                                  <div className="flex justify-center py-4">
                                    <Skeleton className="h-20 w-full" />
                                  </div>
                                ) : taskComments[task.task_id]?.length > 0 ? (
                                  taskComments[task.task_id].map((comment) => (
                                    <div key={comment.comment_id} className="flex gap-3 p-4 bg-[#2d3748] rounded-lg shadow-sm">
                                      <Avatar className="h-8 w-8 flex-shrink-0">
                                        <AvatarFallback className={`${getAvatarColor(comment.user_name)} text-white text-xs font-medium`}>
                                          {getInitials(comment.user_name)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                          <div>
                                            <p className="text-sm font-semibold text-white">{comment.user_name}</p>
                                            <p className="text-xs text-[#9ca3af]">
                                              {format(new Date(comment.created_at), 'MMM dd, yyyy h:mm a')}
                                              {comment.updated_at && ' (edited)'}
                                            </p>
                                          </div>
                                          {comment.user_email === getCurrentUserEmail() && (
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-slate-700">
                                                  <MoreVertical className="w-3 h-3 text-[#9ca3af]" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleStartEditComment(comment)}>
                                                  <Edit2 className="w-3 h-3 mr-2" />
                                                  Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                  onClick={() => handleDeleteComment(task.task_id, comment.comment_id)}
                                                  className="text-red-600"
                                                >
                                                  <Trash2 className="w-3 h-3 mr-2" />
                                                  Delete
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          )}
                                        </div>
                                        {editingComment?.comment_id === comment.comment_id ? (
                                          <div className="mt-2 flex gap-2">
                                            <Input
                                              value={editCommentText}
                                              onChange={(e) => setEditCommentText(e.target.value)}
                                              className="h-8 text-sm bg-white text-slate-900"
                                              placeholder="Edit comment..."
                                              autoFocus
                                            />
                                            <Button
                                              size="sm"
                                              onClick={() => handleSaveEditComment(task.task_id, comment.comment_id)}
                                              className="h-8 bg-[#3182ce] hover:bg-blue-700 text-white"
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                setEditingComment(null);
                                                setEditCommentText('');
                                              }}
                                              className="h-8 bg-white text-slate-900"
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        ) : (
                                          <p className="text-sm mt-2 whitespace-pre-wrap break-words text-white leading-relaxed">{comment.comment_text}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center py-8 bg-[#2d3748] rounded-lg">
                                    <p className="text-sm text-[#9ca3af]">💬 No comments yet. Be the first to comment!</p>
                                  </div>
                                )}
                              </div>

                              {/* Add Comment Input */}
                              <div className="flex gap-2 items-end border-t-2 border-[#e2e8f0] pt-4">
                                <div className="flex-1 relative">
                                  <Textarea
                                    ref={textareaRef}
                                    value={commentText}
                                    onChange={handleCommentTextChange}
                                    placeholder="Add a comment... (Use @ to mention someone)"
                                    className="min-h-[70px] pr-10 resize-none bg-white text-slate-900 border-2 border-[#e2e8f0] placeholder:text-slate-500 focus:border-[#3182ce]"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddComment(task.task_id);
                                      }
                                    }}
                                  />
                                  <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute bottom-2 right-2 h-7 w-7 hover:bg-slate-100"
                                        type="button"
                                      >
                                        <Smile className="w-4 h-4 text-slate-600" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-full p-0" align="end">
                                      <EmojiPicker onEmojiClick={onEmojiClick} width={320} height={400} />
                                    </PopoverContent>
                                  </Popover>
                                  
                                  {/* @mention autocomplete */}
                                  {showMentions && filteredMentionUsers.length > 0 && (
                                    <div className="absolute bottom-full mb-1 left-0 w-64 bg-white border border-slate-300 rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                                      <Command>
                                        <CommandList>
                                          <CommandGroup>
                                            {filteredMentionUsers.map((user) => (
                                              <CommandItem
                                                key={user}
                                                onSelect={() => insertMention(user)}
                                                className="cursor-pointer text-slate-900"
                                              >
                                                <AtSign className="w-3 h-3 mr-2" />
                                                {user}
                                              </CommandItem>
                                            ))}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </div>
                                  )}
                                </div>
                                <Button
                                  onClick={() => handleAddComment(task.task_id)}
                                  disabled={!commentText.trim()}
                                  className="bg-[#3182ce] hover:bg-blue-700 text-white"
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Send
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!descriptionModalTask} onOpenChange={(open) => { if (!open) setDescriptionModalTask(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="task-description-full-modal">
          <DialogHeader>
            <DialogTitle data-testid="task-description-full-modal-title">
              {descriptionModalTask?.title || 'Task Description'}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border p-4 bg-muted/20" data-testid="task-description-full-modal-content">
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {descriptionModalTask?.description || 'No description'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
